import React, { CSSProperties, forwardRef, HTMLAttributes, useMemo } from 'react';
import classNames from 'classnames';
import Color from 'color';

import styles from './Pill.module.scss';

export interface IPillProps
    extends Omit<React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'prefix'> {
    color?: 'primary' | 'success' | 'error' | 'warning' | 'gray' | 'sky' | string;
    size?: 'small' | 'medium' | 'large';
    colorMode?: 'soft' | 'hard';
    rounded?: 'full' | 'normal';
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
}

export const Pill = forwardRef<HTMLDivElement, IPillProps>(
    (
        {
            colorMode = 'soft',
            color = 'primary',
            rounded = 'full',
            size = 'medium',
            className,
            children,
            prefix,
            suffix,
            style,
            ...props
        },
        ref,
    ) => {
        const customPillColor = useMemo<CSSProperties>(() => {
            if (!/^#/.test(color)) return {};
            if (colorMode === 'hard') {
                return { backgroundColor: color, color: '#ffffff' };
            }
            return { backgroundColor: Color(color).alpha(0.15).string(), color: color };
        }, [color]);

        return (
            <div
                ref={ref}
                className={classNames(
                    styles.pill,
                    styles[`size-${size}`],
                    styles[`color-${color}`],
                    styles[`rounded-${rounded}`],
                    styles[`colorMode-${colorMode}`],
                    {
                        [styles.withPrefix]: !!prefix,
                        [styles.withSuffix]: !!suffix,
                    },
                    className,
                )}
                style={{ ...style, ...customPillColor }}
                {...props}
            >
                {prefix}
                {children && <div className={styles.content}>{children}</div>}
                {suffix}
            </div>
        );
    },
);
